import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveTKey } from '../../../../i18n/resolve-key';
import { Box, IconButton, InputAdornment, TextField } from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
import type { FilterControlProps, FilterValue, FilterOperator } from '../filters/filter-registry.js';
import {
  FILTER_OPERATORS,
  getOperatorsForType,
  registerFilterControl,
} from '../filters/filter-registry.js';
import { FilterOperatorMenu } from './FilterOperatorMenu.tsx';

type TextFilterProps = FilterControlProps;

export function TextColumnFilter({ column, value, onChange, disabled }: TextFilterProps) {
  const { t } = useTranslation();
  const accessibleLabel = t(resolveTKey(column.labelKey));
  const allowedOperators = useMemo(() => {
    const allowed = column.allowedFilterOperators;
    const candidates = getOperatorsForType('text');
    return allowed ? candidates.filter(operator => allowed.includes(operator.id)) : candidates;
  }, [column.allowedFilterOperators]);
  const defaultOperator = allowedOperators[0]?.id ?? 'contains';
  const [localValue, setLocalValue] = useState<string>(value?.value == null ? '' : String(value.value));
  const [localOperator, setLocalOperator] = useState<FilterOperator>(value?.operator ?? defaultOperator);

  useEffect(() => {
    if (value) {
      setLocalValue(value.value == null ? '' : String(value.value));
      setLocalOperator(value.operator);
    } else {
      setLocalValue('');
      setLocalOperator(defaultOperator);
    }
  }, [defaultOperator, value]);

  const operatorDescriptor =
    FILTER_OPERATORS.find(operator => operator.id === localOperator) ?? allowedOperators[0];
  const requiresValue = operatorDescriptor?.requiresValue !== false;

  const emit = (operator: FilterOperator, nextValue: string) => {
    const descriptor = FILTER_OPERATORS.find(candidate => candidate.id === operator);
    if (descriptor?.requiresValue === false) {
      onChange({ operator, value: null });
      return;
    }
    onChange(nextValue ? { operator, value: nextValue } : null);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    setLocalValue(nextValue);
    emit(localOperator, nextValue);
  };

  const handleOperatorChange = (operator: string) => {
    const nextOperator = operator as FilterOperator;
    setLocalOperator(nextOperator);
    emit(nextOperator, localValue);
  };

  const handleClear = () => {
    setLocalValue('');
    onChange(null);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0 }}>
      {allowedOperators.length > 1 && (
        <FilterOperatorMenu
          column={column}
          currentOperator={localOperator}
          onChange={handleOperatorChange}
          allowedOperators={allowedOperators}
          disabled={disabled}
        />
      )}
      {requiresValue ? (
        <TextField
          size="small"
          variant="outlined"
          fullWidth
          type="text"
          placeholder={accessibleLabel}
          value={localValue}
          onChange={handleChange}
          disabled={disabled}
          sx={{ minWidth: 0 }}
          slotProps={{
            htmlInput: {
              'aria-label': accessibleLabel,
            },
            input: {
              endAdornment: localValue ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClear}
                    disabled={disabled}
                    aria-label={t('common:clear')}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
        />
      ) : (
        <Box sx={{ flex: 1, minWidth: 0 }} />
      )}
    </Box>
  );
}

function createTextFilter() {
  return React.memo(TextColumnFilter);
}

registerFilterControl('text', () => createTextFilter());
